import { useRef, useState } from 'react';
import { Box, Button, Paper, Stack, TextareaAutosize, Typography } from '@mui/material';
import { TextField } from 'formik-mui';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { SwitchField } from 'shared/ui/formik-fields/switch-field';
import { formTools, PublishingDataFormData } from './publishing-data.form-tools';
import {
  PublishingDataRelations,
  useAddPublishingData,
  usePublishingData,
  useUpdatePublishingData
} from 'entities/publishing-data/publishing-data.hooks';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { ContentProviderRef, RichEditor } from 'shared/ui/rich-editor/rich-editor';
import FilePickerField from 'shared/ui/formik-fields/file-picker';
import { useSnack } from 'shared/ui/snack';
import { MediaBasicDataDto } from '@sr/dto';
import { LinkToMedia } from 'shared/ui/links/link-to-media';

type Props = {
    id: number | null,
    relatedType: PublishingDataRelations,
    relatedId: number,
    media?: MediaBasicDataDto,
    specialPlacement?: boolean,
}

export const PublishingDataForm = ({ id, relatedType, relatedId, media, specialPlacement = false }: Props) => {
  const shortDescription = useRef<ContentProviderRef>(null);
  const fullDescription = useRef<ContentProviderRef>(null);
  const benefits = useRef<ContentProviderRef>(null);
  const specificConditions = useRef<ContentProviderRef>(null);

  const [rewriteShortDescription, setRewriteShortDescription] = useState(false);
  const [rewriteFullDescription, setRewriteFullDescription] = useState(false);
  const [rewriteBenefits, setRewriteBenefits] = useState(false);
  const [rewriteSpecificConditions, setRewriteSpecificConditions] = useState(false);

  const { addPublishingData, isUpdating } = useAddPublishingData(relatedType, relatedId);
  const updatePublishingData = useUpdatePublishingData();
  const { showSuccessUpdate, showFailedUpdate } = useSnack({
    itemTitleForm1: 'Данные сайта', itemTitleForm2: 'Данных сайта', gender: 'plural'
  });

  const { item: serverPublishingData, isLoading, error } = usePublishingData(id);
  
  const matherPublishingDataId = media?.parentMedia?.publishingDataId ?? null;
  const { item: serverMatherPublishingData } = usePublishingData(matherPublishingDataId);

  const isShortDescriptionFromMather = !serverPublishingData?.shortDescription && !!serverMatherPublishingData?.shortDescription;
  const isFullDescriptionFromMather = !serverPublishingData?.fullDescription && !!serverMatherPublishingData?.fullDescription;
  const isBenefitsFromMather = !serverPublishingData?.benefits && !!serverMatherPublishingData?.benefits;
  const isSpecificConditionsFromMather = !serverPublishingData?.specificConditions && !!serverMatherPublishingData?.specificConditions;

  const handleSubmit = async (formData: PublishingDataFormData) => {
    const submitData: PublishingDataFormData = {
      ...formData,
      shortDescription: rewriteShortDescription 
        ? (shortDescription.current?.() ?? '')
        : (isShortDescriptionFromMather ? '' : (shortDescription.current?.() ?? '')),
      fullDescription: rewriteFullDescription 
        ? (fullDescription.current?.() ?? '')
        : (isFullDescriptionFromMather ? '' : (fullDescription.current?.() ?? '')),
      benefits: rewriteBenefits 
        ? (benefits.current?.() ?? '')
        : (isBenefitsFromMather ? '' : (benefits.current?.() ?? '')),
      specificConditions: specialPlacement 
        ? formData.specificConditions 
        : (rewriteSpecificConditions 
          ? (specificConditions.current?.() ?? '')
          : (isSpecificConditionsFromMather ? '' : (specificConditions.current?.() ?? '')))
    };

    if (serverPublishingData) {
      return await updatePublishingData(formTools.toUpdateDto(serverPublishingData.id, submitData))
        .then(() => {
          showSuccessUpdate();
        })
        .catch((error) => {
          showFailedUpdate(error);
        });
    }
    return await addPublishingData(formTools.toCreateDto(submitData))
      .then(() => {
        showSuccessUpdate();
      })
      .catch((error) => {
        showFailedUpdate(error);
      });
  };

  let initialData = formTools.emptyValues();

  const baseData: PublishingDataFormData = {
    slug: serverPublishingData?.slug || '',
    shortDescription: serverPublishingData?.shortDescription || '',
    fullDescription: serverPublishingData?.fullDescription || '',
    benefits: serverPublishingData?.benefits || '',
    specificConditions: serverPublishingData?.specificConditions || '',
    smallImage: serverPublishingData?.smallImage ? [serverPublishingData.smallImage].flat() : [],
    detailedImage: serverPublishingData?.detailedImage ? [serverPublishingData.detailedImage].flat() : [],
    isPublished: serverPublishingData?.isPublished || false,
    isSubdomain: serverPublishingData?.isSubdomain || false
  };

  if (serverMatherPublishingData) {
    const mergedData: PublishingDataFormData = { ...baseData };

    if (!serverPublishingData?.shortDescription && serverMatherPublishingData.shortDescription) {
      mergedData.shortDescription = serverMatherPublishingData.shortDescription;
    }

    if (!serverPublishingData?.fullDescription && serverMatherPublishingData.fullDescription) {
      mergedData.fullDescription = serverMatherPublishingData.fullDescription;
    }

    if (!serverPublishingData?.benefits && serverMatherPublishingData.benefits) {
      mergedData.benefits = serverMatherPublishingData.benefits;
    }

    if (!serverPublishingData?.specificConditions && serverMatherPublishingData.specificConditions) {
      mergedData.specificConditions = serverMatherPublishingData.specificConditions;
    }

    if (serverPublishingData) {
      const dtoCompatibleData = {
        ...serverPublishingData,
        slug: mergedData.slug,
        shortDescription: mergedData.shortDescription,
        fullDescription: mergedData.fullDescription,
        benefits: mergedData.benefits,
        specificConditions: mergedData.specificConditions,
        isPublished: mergedData.isPublished,
        isSubdomain: mergedData.isSubdomain,
        smallImage: serverPublishingData.smallImage,
        detailedImage: serverPublishingData.detailedImage
      };
      initialData = formTools.fromDto(dtoCompatibleData);
    } else {
      initialData = {
        ...mergedData,
        smallImage: serverMatherPublishingData.smallImage ? [serverMatherPublishingData.smallImage] : [],
        detailedImage: serverMatherPublishingData.detailedImage ? [serverMatherPublishingData.detailedImage] : []
      };
    }
  } else if (serverPublishingData) {
    initialData = formTools.fromDto(serverPublishingData);
  }

  return (
    <>
      {error?.length
        ? error
        : isLoading
          ? <LoadingBanner/>
          : <Formik initialValues={initialData} onSubmit={handleSubmit} validate={formTools.validate}>
            {({ submitForm, isSubmitting, isValid, dirty, values }: FormikProps<PublishingDataFormData>) => (
              <Paper sx={{ p: 1, pt: 2 }}>
                <Box
                  sx={{ py: 1, mb: 1, justifyContent: 'space-between', display: 'flex', gap: 1 }}>
                  <Field sx={{ width: 1 }} name='slug' label='Символьный код'
                    component={TextField}/>
                </Box>
                <Stack direction="row" justifyContent="space-between">
                  <Box sx={{ width: 1 / 2 }}>
                    <Field name='isPublished' label='Опубликовано на сайте'
                      type="checkbox"
                      component={SwitchField}/>
                  </Box>
                  <Box sx={{ width: 1 / 2 }}>
                    <Field name='isSubdomain' label='Как субдомен'
                      type="checkbox"
                      component={SwitchField}/>
                  </Box>
                </Stack>
                
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Краткое описание</Typography>
                    {isShortDescriptionFromMather && (
                      <Field 
                        name='rewriteShortDescription'
                        label='Переписать описание'
                        type="checkbox"
                        component={SwitchField}
                        checked={rewriteShortDescription}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                          setRewriteShortDescription(e.target.checked)
                        }
                      />
                    )}
                  </Stack>
                  <Field name='shortDescription'>
                    {({ field, form, meta }: FieldProps) => (
                      <RichEditor 
                        content={field.value} 
                        disabled={isSubmitting || (isShortDescriptionFromMather && !rewriteShortDescription)}
                        ref={shortDescription}
                        onChange={() => {
                          if (!dirty)
                            form.setFieldValue('shortDescription', shortDescription.current?.());
                        }}
                      />
                    )}
                  </Field>
                  {isShortDescriptionFromMather && !rewriteShortDescription && media?.parentMedia && (
                    <Typography variant="caption" color="text.secondary">
                      Используются данные из материнского СМИ(<LinkToMedia media={media.parentMedia}>
                        {media.parentMedia.name}
                      </LinkToMedia>). Включите "Переписать описание" для редактирования.
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Подробное описание</Typography>
                    {isFullDescriptionFromMather && (
                      <Field 
                        name='rewriteFullDescription'
                        label='Переписать описание'
                        type="checkbox"
                        component={SwitchField}
                        checked={rewriteFullDescription}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                          setRewriteFullDescription(e.target.checked)
                        }
                      />
                    )}
                  </Stack>
                  <Field name='fullDescription'>
                    {({ field, form, meta }: FieldProps) => (
                      <RichEditor 
                        content={field.value} 
                        disabled={isSubmitting || (isFullDescriptionFromMather && !rewriteFullDescription)}
                        ref={fullDescription}
                        onChange={() => {
                          if (!dirty)
                            form.setFieldValue('fullDescription', fullDescription.current?.());
                        }}
                      />
                    )}
                  </Field>
                  {isFullDescriptionFromMather && !rewriteFullDescription && media?.parentMedia && (
                    <Typography variant="caption" color="text.secondary">
                      Используются данные из материнского СМИ(<LinkToMedia media={media.parentMedia}>
                        {media.parentMedia.name}
                      </LinkToMedia>). Включите "Переписать описание" для редактирования.
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Преимущества типа СМИ</Typography>
                    {isBenefitsFromMather && (
                      <Field 
                        name='rewriteBenefits'
                        label='Переписать описание'
                        type="checkbox"
                        component={SwitchField}
                        checked={rewriteBenefits}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                          setRewriteBenefits(e.target.checked)
                        }
                      />
                    )}
                  </Stack>
                  <Field name='benefits'>
                    {({ field, form, meta }: FieldProps) => (
                      <RichEditor 
                        content={field.value} 
                        disabled={isSubmitting || (isBenefitsFromMather && !rewriteBenefits)} 
                        ref={benefits}
                        onChange={() => {
                          if (!dirty)
                            form.setFieldValue('benefits', benefits.current?.());
                        }}
                      />
                    )}
                  </Field>
                  {isBenefitsFromMather && !rewriteBenefits && media?.parentMedia && (
                    <Typography variant="caption" color="text.secondary">
                      Используются данные из материнского СМИ(<LinkToMedia media={media.parentMedia}>
                        {media.parentMedia.name}
                      </LinkToMedia>). Включите "Переписать описание" для редактирования.
                    </Typography>
                  )}
                </Box>

                <Box>
                  {
                    specialPlacement ? (
                      <>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="h6">Особые условия размещения (данные используются для особого соглашения в приложении)</Typography>
                        </Stack>
                        <Field name='specificConditions'>
                          {({ field, form, meta }: FieldProps) => (
                            <TextareaAutosize
                              {...field}
                              minRows={4}
                              maxRows={8}
                              placeholder="Введите особые условия размещения"
                              style={{
                                width: '100%',
                                padding: '16.5px 14px',
                                borderColor: meta.touched && meta.error ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)',
                                borderRadius: '4px',
                                fontSize: '1rem',
                                fontFamily: 'inherit',
                                resize: 'vertical'
                              }}
                              onChange={(e) => form.setFieldValue('specificConditions', e.target.value)}
                            />
                          )}
                        </Field>
                      </>
                    ) : (
                      <>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="h6">Особые условия размещения</Typography>
                          {isSpecificConditionsFromMather && (
                            <Field 
                              name='rewriteSpecificConditions'
                              label='Переписать описание'
                              type="checkbox"
                              component={SwitchField}
                              checked={rewriteSpecificConditions}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                setRewriteSpecificConditions(e.target.checked)
                              }
                            />
                          )}
                        </Stack>
                        <Field name='specificConditions'>
                          {({ field, form, meta }: FieldProps) => (
                            <RichEditor 
                              content={field.value} 
                              disabled={isSubmitting || (isSpecificConditionsFromMather && !rewriteSpecificConditions)}
                              ref={specificConditions}
                              onChange={() => {
                                if (!dirty)
                                  form.setFieldValue('specificConditions', specificConditions.current?.());
                              }}
                            />
                          )}
                        </Field>
                        {isSpecificConditionsFromMather && !rewriteSpecificConditions && media?.parentMedia && (
                          <Typography variant="caption" color="text.secondary">
                      Используются данные из материнского СМИ(<LinkToMedia media={media.parentMedia}>
                              {media.parentMedia.name}
                            </LinkToMedia>). Включите "Переписать описание" для редактирования.
                          </Typography>
                        )}
                      </>
                    )}
                </Box>    

                <Box sx={{ p: 1, pt: 3, display: 'flex', flexDirection: 'row', gap: 5 }}>
                  <Field
                    sx={{ width: 1 / 2 }}
                    name="smallImage"
                    label="Маленький логотип"
                    pickerType='image'
                    fileCategory={`${relatedType}-small-logo`}
                    isPublicFile={true}
                    component={FilePickerField}
                  />
                  <Field
                    sx={{ width: 1 / 2 }}
                    name="detailedImage"
                    label="Логотип"
                    pickerType='image'
                    fileCategory={`${relatedType}-detailed-image`}
                    isPublicFile={true}
                    component={FilePickerField}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={submitForm}
                  disabled={isSubmitting || !isValid || !dirty || isUpdating}
                >
                  сохранить
                </Button>
              </Paper>
            )}
          </Formik>
      }
    </>
  );
};
